import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  ReactNode,
  useCallback,
} from "react";
import { env } from "@/config/env";
import type { User, AuthState } from "@/shared/types/auth.types";
import { SUBCONTRACTOR_TYPE } from "@/shared/types/auth.types";
import { useTenantContext } from "./TenantProvider";
import { FeatureToggleService } from "@/shared/services/featureToggle.service";
import {
  apiClient as defaultApiClient,
  createTenantApiClient,
} from "@/shared/api/client";
import { apiRoutes } from "@/shared/api/routes";
import type { AxiosInstance } from "axios";
import { setServerTimeContext } from "@/shared/utils/serverTimeContext";

/**
 * Base permissions that are restricted for Normal, Subcontractor, and Funding Source users.
 * Based on existing app behavior shown in screenshots.
 */
const RESTRICTED_PERMISSIONS_BASE = [
  'configuration_create',           // Configuration Parameters - Create only (View/Update allowed)
  'allowed_value_set_view',         // Custom Values - View
  'allowed_value_set_create',       // Custom Values - Create
  'allowed_value_set_update',       // Custom Values - Update
  'allowed_value_set_delete',       // Custom Values - Delete
  'report_template_update',         // Report Templates - Update (View is allowed)
  'metaroute_custom_field_view',    // Schedule Custom Fields - View
  'metaroute_custom_field_create',  // Schedule Custom Fields - Create
  'metaroute_custom_field_update',  // Schedule Custom Fields - Update
  'metaroute_custom_field_delete',  // Schedule Custom Fields - Delete
  'batch_job_delete',               // Batch Jobs - Delete (View, Create, Update are allowed)
];

/**
 * Additional module permissions restricted for Normal, Subcontractor, and Funding Source users.
 * Center users (type 3) have ACCESS to these modules.
 */
const RESTRICTED_MODULE_PERMISSIONS = [
  'bulk_import',                    // Bulk Import module
  'edi',                            // EDI module
  'reporting',                      // Reporting module
];

/**
 * Combined restrictions for Normal (0), Subcontractor (1), and Funding Source (2) users.
 */
const RESTRICTED_PERMISSIONS_NORMAL_SUBCONTRACTOR_FUNDING = [
  ...RESTRICTED_PERMISSIONS_BASE,
  ...RESTRICTED_MODULE_PERMISSIONS,
];

/**
 * Restrictions for Center User (3) - same base restrictions but WITH access to modules.
 */
const RESTRICTED_PERMISSIONS_CENTER_USER = [
  ...RESTRICTED_PERMISSIONS_BASE,
  // Center users have ACCESS to Bulk import, EDI, and Reporting (not restricted)
];

interface AuthContextType extends AuthState {
  bootstrapSession: () => Promise<boolean>;
  /**
   * ECOPD-14897: Re-authenticates with fresh credentials (login + new password)
   * without navigating to /login. Calls POST /drt/api/login to produce a new
   * RoR session (with fresh k3 credentials), then bootstraps session state.
   * Used after a self-password-change so the user stays logged in.
   */
  relogin: (login: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  apiClient: AxiosInstance;
  permissions: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

interface SessionBootstrapResponse {
  user_id?: number;
  id?: number;
  email?: string;
  login?: string;
  user_name?: string;
  user_type?: string;
  userType?: string;
  roles?: string[];
  permissions?: string[];
  tenant_id?: string;
  tenantId?: string;
  subcontractor?: number;
  company_ids?: number[];
  funding_source_ids?: number[];
  center_id?: number | null;
  default_run_pool_id?: number | null;
  access_unassigned_no_run_trips?: boolean;
  server_now?: string;
  server_today?: string;
  server_timezone?: string;
}

/**
 * Filter out restricted permissions based on user type.
 * - Normal (0), Subcontractor (1), Funding Source (2): Same restrictions including module access
 * - Center User (3): Base restrictions but WITH access to Bulk import, EDI, and Reporting
 * - Ecolane (4): No restrictions (full admin access)
 */
const filterPermissionsByUserType = (permissions: string[], subcontractorType?: number): string[] => {
  const userType = subcontractorType ?? SUBCONTRACTOR_TYPE.NORMAL;
  
  // Ecolane users get all permissions (no restrictions)
  if (userType === SUBCONTRACTOR_TYPE.ECOLANE) {
    return permissions;
  }
  
  // Center User (3): Base restrictions but WITH access to modules
  if (userType === SUBCONTRACTOR_TYPE.CLIENT_POOL) {
    return permissions.filter(permission => !RESTRICTED_PERMISSIONS_CENTER_USER.includes(permission));
  }
  
  // Normal (0), Subcontractor (1), Funding Source (2): Same restrictions
  if (
    userType === SUBCONTRACTOR_TYPE.NORMAL ||
    userType === SUBCONTRACTOR_TYPE.COMPANY ||
    userType === SUBCONTRACTOR_TYPE.FUNDING_SOURCE
  ) {
    return permissions.filter(permission => !RESTRICTED_PERMISSIONS_NORMAL_SUBCONTRACTOR_FUNDING.includes(permission));
  }
  
  // Default: return all permissions (should not reach here)
  return permissions;
};

const normalizeSessionUser = (session: SessionBootstrapResponse, tenantId?: string): User => {
  const rawPermissions = Array.isArray(session?.permissions) ? session.permissions : [];
  const subcontractorType = session?.subcontractor;
  
  return {
    id: String(session?.user_id ?? session?.id ?? ""),
    email: session?.email ?? session?.login ?? "",
    name: session?.user_name ?? session?.login ?? "",
    userType: session?.user_type ?? session?.userType ?? "",
    roles: Array.isArray(session?.roles) ? session.roles : [],
    permissions: filterPermissionsByUserType(rawPermissions, subcontractorType),
    tenantId: session?.tenant_id ?? session?.tenantId ?? tenantId ?? "default",
    ...(subcontractorType !== undefined ? { subcontractor: subcontractorType } : {}),
    companyIds: session?.company_ids ?? [],
    fundingSourceIds: session?.funding_source_ids ?? [],
    centerId: session?.center_id ?? null,
    defaultRunPoolId: session?.default_run_pool_id ?? null,
    accessUnassignedNoRunTrips: session?.access_unassigned_no_run_trips ?? false,
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { tenantId } = useTenantContext();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
    error: null,
  });

  const tenantIdRef = useRef(tenantId);

  // Create tenant-aware API client
  const apiClient = tenantId ? createTenantApiClient(tenantId) : defaultApiClient;

  const bootstrapSession = useCallback(async (): Promise<boolean> => {
    const response = await fetch(apiRoutes.session(), {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (response.status === 401) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      });
      return false;
    }

    if (!response.ok) {
      throw new Error(`Session request failed with status ${response.status}`);
    }

    const sessionData: SessionBootstrapResponse = await response.json();
    setServerTimeContext({
      serverNow: sessionData.server_now ?? null,
      serverToday: sessionData.server_today ?? null,
      serverTimezone: sessionData.server_timezone ?? null,
    });
    const user = normalizeSessionUser(sessionData, tenantIdRef.current);

    setAuthState({
      user,
      isAuthenticated: true,
      loading: false,
      error: null,
    });

    return true;
  }, []);

  // ECOPD-14897: After a self-password-change, k3 invalidates the user's auth
  // token stored inside the RoR session.  The only way to get fresh k3 credentials
  // without forcing the user through /login is to POST to the login endpoint with
  // the new password, which makes RoR create a new session (new cookie + new k3
  // token), and then call bootstrapSession() to refresh auth state.
  const relogin = useCallback(async (login: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch(apiRoutes.login(), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ login, password }),
      });

      if (!response.ok) {
        return false;
      }

      // Refresh auth state with the new session
      return await bootstrapSession();
    } catch {
      return false;
    }
  }, [bootstrapSession]);

  const logout = async () => {
    try {
      await defaultApiClient.post(apiRoutes.logout());
    } catch (_error) {
      // Ignore logout errors and force local signed-out state.
    } finally {
      setAuthState({
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      });
      window.location.assign("/login");
    }
  };

  // Check if user has specific permission
  const can = (permission: string): boolean => {
    return authState.user?.permissions?.includes(permission) || false;
  };

  // Check if user has specific role
  const hasRole = (role: string): boolean => {
    return authState.user?.roles?.includes(role) || false;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((role) => hasRole(role));
  };

  // Initialize auth state on mount
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      // Initialize feature toggle service with mock data in development
      if (env.NODE_ENV === "development") {
        const featureService = FeatureToggleService.getInstance();
        featureService.initializeMockData();
      }

      try {
        await bootstrapSession();
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setAuthState({
          user: null,
          isAuthenticated: false,
          loading: false,
          error: error instanceof Error ? error.message : "Failed to load session",
        });
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [bootstrapSession]);

  // ECOPD-14897: Listen for genuine HTTP 401 responses from the axios interceptor.
  // Before hard-redirecting to /login, attempt bootstrapSession() to confirm the
  // session is truly gone.  Self-password-change scenarios are handled separately
  // via relogin() in useUpdateUser — not here.
  useEffect(() => {
    let handling = false;

    const handleSessionExpired = async () => {
      // Debounce: if multiple 401s fire in quick succession only handle once.
      if (handling) return;
      handling = true;
      try {
        const ok = await bootstrapSession();
        if (!ok) {
          // RoR session is gone — redirect to login.
          window.location.assign("/login");
        }
      } catch {
        window.location.assign("/login");
      } finally {
        handling = false;
      }
    };

    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, [bootstrapSession]);

  const contextValue = useMemo<AuthContextType>(() => ({
    ...authState,
    bootstrapSession,
    relogin,
    logout,
    can,
    hasRole,
    hasAnyRole,
    apiClient,
    permissions: authState.user?.permissions || [],
  }), [authState, bootstrapSession, relogin, logout, can, hasRole, hasAnyRole, apiClient]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
